"use client"
import React, { useMemo, useTransition, Suspense } from "react";
import { apiFetch } from "@/utils/api";
import { useRouter, useSearchParams } from "next/navigation";
import ErrorState from "@/components/ui/ErrorState";
import EmptyState from "@/components/ui/EmptyState";
// import Image from "next/image";
import { useInView } from "react-intersection-observer";
import OfferCard from "@/components/ui/OfferCard";
import { ChevronDown, ChevronUp, Check } from "lucide-react";
import { SearchIcon } from "@/utils/icons";

interface Option {
  label: string;
  value: string | number;
}

interface Category {
  _id: string;
  name: string;
  slug: string;
  icon: string;
  description: string;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface Store {
  _id: string;
  name: string;
  slug: string;
  icon?: string;
}

interface MobileSelectDropdownProps {
  label: string;
  options: Option[];
  currentValue: string | number;
  onChange: (newValue: string | number) => void;
}

const MobileSelectDropdown: React.FC<MobileSelectDropdownProps> = ({
  label,
  options,
  currentValue,
  onChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close the dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (value: string | number) => {
    onChange(value);
    setIsOpen(false);
  };

  const selectedOption = options.find(o => o.value === currentValue) || options[0];

  return (
    <div className="relative flex-1 min-w-[30%]" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-2 px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 hover:bg-gray-50 transition-colors w-full"
      >
        <div className="flex items-center gap-1">
          <span className="font-semibold text-gray-700 whitespace-nowrap">{label}:</span>
          <span className="truncate">{selectedOption?.label || 'Select'}</span>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
      </button>

      {isOpen && (
        <div
          className="absolute z-40 top-full mt-2 w-full rounded-lg shadow-xl bg-white border border-gray-200 right-0"
        >
          <div className="py-1 max-h-60 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`w-full text-left px-4 py-2 text-sm flex justify-between items-center transition-colors duration-150 ${currentValue == option.value // Use loose comparison for string/number values
                  ? "bg-primary-50 text-primary-600 font-medium"
                  : "hover:bg-gray-100 text-gray-700"
                  }`}
              >
                {option.label}
                {currentValue == option.value && (
                  <Check className="w-4 h-4" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface Offer {
  _id?: string;
  store: string;
  storeIcon?: string;
  title: string;
  slug: string;
  description: string;
  discount: string;
  discountType: string;
  discountTypeFlat: string;
  discountTypeUnit: string;
  oldValue: string;
  oldValueUnit: "price" | "percentage";
  category: string;
  couponCode: string;
  validTill: string;
  usedBy: number;
  rating: number;
  color: string;
  badge: string;
  bgGradient: string;
  isActive: boolean;
  allowRedirectUrl: boolean;
  redirectUrl: string;
}

const SORT_OPTIONS: Option[] = [
  { label: "Newest", value: "createdAt-desc" },
  { label: "Best Discount", value: "discount-desc" },
  { label: "Highest Rated", value: "rating-desc" },
  { label: "Most Used", value: "usedBy-desc" },
  { label: "Oldest", value: "createdAt-asc" },
];

const TYPE_OPTIONS: Option[] = [
  { label: "All", value: "" },
  { label: "Code", value: "code" },
  { label: "General", value: "general" },
];

const RATING_OPTIONS: Option[] = [
  { label: "Any", value: 0 },
  { label: "1+", value: 1 },
  { label: "2+", value: 2 },
  { label: "3+", value: 3 },
  { label: "4+", value: 4 },
  { label: "5", value: 5 },
];


function ClientOffersPage() {
  const searchParams = useSearchParams() as URLSearchParams;
  function getStringParam(param: string | string[] | null | undefined, fallback = ""): string {
    if (Array.isArray(param)) return param[0] || fallback;
    if (typeof param === "string") return param;
    return fallback;
  }
  function getNumberParam(param: string | string[] | null | undefined, fallback = 0): number {
    const str = getStringParam(param);
    const num = Number(str);
    return isNaN(num) ? fallback : num;
  }
  const initialSort = getStringParam(searchParams.get("sort"), "usedBy");
  const initialOrder = getStringParam(searchParams.get("order"), "desc");
  const initialMinDiscount = getNumberParam(searchParams.get("minDiscount"), 0);
  const initialMaxDiscount = getNumberParam(searchParams.get("maxDiscount"), 100) || 100;
  const initialMinRating = getNumberParam(searchParams.get("minRating"), 0);
  const initialSearch = getStringParam(searchParams.get("search"), "");
  const initialDiscountType = getStringParam(searchParams.get("discountType"), "");

  // Category state (retains array for now)
  const initialCategory = searchParams.getAll("category").length > 0 ? [getStringParam(searchParams.getAll("category"))] : [];

  const initialStore = getStringParam(searchParams.get("store"), "");

  const [offers, setOffers] = React.useState<Offer[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [allCategoryOptions, setAllCategoryOptions] = React.useState<Option[]>([]);
  const [allStoreOptions, setAllStoreOptions] = React.useState<Option[]>([]); // ✅ NEW Store Options State

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [offerCategoriesFromOffers, setOfferCategoriesFromOffers] = React.useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = React.useState(initialCategory);
  const [selectedStores, setSelectedStores] = React.useState<string>(initialStore);

  const [search, setSearch] = React.useState(initialSearch);
  const [discountType, setDiscountType] = React.useState(initialDiscountType);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);
  const [minDiscount, setMinDiscount] = React.useState(initialMinDiscount);
  const [maxDiscount, setMaxDiscount] = React.useState(initialMaxDiscount);
  const [minRating, setMinRating] = React.useState(initialMinRating);
  const [sort, setSort] = React.useState(initialSort);
  const [order, setOrder] = React.useState(initialOrder);
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  const { ref: loadMoreRef, inView } = useInView({ triggerOnce: false });
  const router = useRouter();
  const searchParamsHook = useSearchParams();
  const [, startTransition] = useTransition();

  const currentSortValue = `${sort}-${order}`;

  React.useEffect(() => {
    apiFetch<{ success: boolean; data: Category[] }>(`/categories/?isActive=true`)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) {
          const categoryOptions: Option[] = res.data
            .filter(c => c.name && c.slug)
            .map(c => ({
              label: c.name.trim(),
              value: c.slug.trim(),
            }));

          setAllCategoryOptions(categoryOptions);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch all categories:", err);
      });
  }, []);

  React.useEffect(() => {
    apiFetch<{ success: boolean; data: Store[] }>(`/stores/?isActive=true`)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) {
          const storeOptions: Option[] = res.data
            .filter(s => s.name && s.slug) 
            .map(s => ({
              label: s.name.trim(), 
              value: s.slug.trim(), 
            }));
          
          setAllStoreOptions(storeOptions); 
        }
      })
      .catch((err) => {
        console.error("Failed to fetch all stores:", err);
      });
  }, []); 

  React.useEffect(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: "1",
      limit: "50",
      sort,
      order,
      minDiscount: String(minDiscount),
      maxDiscount: String(maxDiscount),
      minRating: String(minRating),
      search,
      discountType,
    });
    if (selectedCategories.length > 0) {
      params.append("category", selectedCategories[0]);
    }

    if (selectedStores) params.append("store", selectedStores);

    apiFetch<unknown>(`/offers/?${params.toString()}`)
      .then((res) => {
        let arr: unknown = [];
        let totalPages = 1;
        if (Array.isArray(res)) {
          arr = res;
        } else if (res && typeof res === 'object') {
          const obj = res as Record<string, unknown>;
          if (obj.data && typeof obj.data === 'object' && Array.isArray((obj.data as Record<string, unknown>).offers)) {
            arr = (obj.data as Record<string, unknown>).offers;
            // Type guard for pagination
            const dataObj = obj.data as Record<string, unknown>;
            if (
              'pagination' in dataObj &&
              typeof dataObj.pagination === 'object' &&
              dataObj.pagination !== null &&
              'totalPages' in (dataObj.pagination as Record<string, unknown>)
            ) {
              const pag = dataObj.pagination as Record<string, unknown>;
              totalPages = typeof pag.totalPages === 'number' ? pag.totalPages : 1;
            }
          } else if (Array.isArray(obj.offers)) {
            arr = obj.offers;
          } else if (Array.isArray(obj.data)) {
            arr = obj.data;
          }
        }
        setOffers(Array.isArray(arr) ? arr as Offer[] : []);
        setHasMore(totalPages > 1);
        setPage(1);
        setError(null);
      })
      .catch((err) => {
        setError(err.message || "Failed to load offers");
        setOffers([]);
      })
      .finally(() => setLoading(false));
  }, [sort, order, minDiscount, maxDiscount, minRating, search, discountType, selectedCategories, selectedStores]);

  React.useEffect(() => {
    if (!hasMore || isFetchingMore || loading || !inView) return;
    setIsFetchingMore(true);
    const params = new URLSearchParams({
      page: String(page + 1),
      limit: "50",
      sort,
      order,
      minDiscount: String(minDiscount),
      maxDiscount: String(maxDiscount),
      minRating: String(minRating),
      search,
      discountType,
    });
    if (selectedCategories.length > 0) {
      params.append("category", selectedCategories[0]);
    }

    if (selectedStores) params.append("store", selectedStores);

    apiFetch<unknown>(`/offers/?${params.toString()}`)
      .then((res) => {
        let arr: unknown = [];
        let totalPages = page + 1;
        if (Array.isArray(res)) {
          arr = res;
        } else if (res && typeof res === 'object') {
          const obj = res as Record<string, unknown>;
          if (obj.data && typeof obj.data === 'object' && Array.isArray((obj.data as Record<string, unknown>).offers)) {
            arr = (obj.data as Record<string, unknown>).offers;
            // Type guard for pagination
            const dataObj = obj.data as Record<string, unknown>;
            if (
              'pagination' in dataObj &&
              typeof dataObj.pagination === 'object' &&
              dataObj.pagination !== null &&
              'totalPages' in (dataObj.pagination as Record<string, unknown>)
            ) {
              const pag = dataObj.pagination as Record<string, unknown>;
              totalPages = typeof pag.totalPages === 'number' ? pag.totalPages : (page + 1);
            }
          } else if (Array.isArray(obj.offers)) {
            arr = obj.offers;
          } else if (Array.isArray(obj.data)) {
            arr = obj.data;
          }
        }
        // setOffers(prev => [...prev, ...(Array.isArray(arr) ? arr as Offer[] : [])]);
        setOffers(prev => {
          const existingIds = new Set(prev.map(o => o._id));
          const newOffers = (arr as Offer[]).filter(o => !existingIds.has(o._id));
          return [...prev, ...newOffers];
        });

        setHasMore(totalPages > page + 1);
        setPage(prev => prev + 1);
      })
      .catch(() => setHasMore(false))
      .finally(() => setIsFetchingMore(false));
  }, [hasMore, isFetchingMore, loading, inView, page, sort, order, minDiscount, maxDiscount, minRating, search, discountType, selectedCategories, selectedStores]);

  React.useEffect(() => {
    const uniqueCategories = Array.from(new Set(offers.map(o => o.category).filter(Boolean)));
    setOfferCategoriesFromOffers(uniqueCategories);
  }, [offers]);

  // Filtered offers (client-side, for search, category, store)
  // const filteredOffers = useMemo(() => {
  //   return offers.filter(o =>
  //     // Note: selectedCategories holds the original-case string from the dropdown.
  //     (selectedCategories.length === 0 || selectedCategories.includes(o.category)) &&
  //     // Store filtering logic (now checks for a single string)
  //     (selectedStores === "" || selectedStores === o.store) &&
  //     (search === "" || discountType === "" || o.title.toLowerCase().includes(search.toLowerCase()) || o.store.toLowerCase().includes(search.toLowerCase())) &&
  //     (minRating === 0 || o.rating >= minRating)
  //   );
  // }, [offers, selectedCategories, selectedStores, search, discountType, minRating]);

  const filteredOffers = useMemo(() => {
    const filterCategory = selectedCategories[0] || ""; // Filter by the selected slug

    return offers.filter(o =>
      (filterCategory === "" || o.category === filterCategory) &&

      (selectedStores === "" || o.store === selectedStores) && 
      (search === "" || discountType === "" || o.title.toLowerCase().includes(search.toLowerCase()) || o.store.toLowerCase().includes(search.toLowerCase())) &&
      (minRating === 0 || o.rating >= minRating)
    );
  }, [offers, selectedCategories, selectedStores, search, discountType, minRating]);

  // Handle sort change (for both native select and custom dropdown)
  const handleSortChange = (newSortValue: string | number) => {
    const [newSort, newOrder] = String(newSortValue).split("-");
    setSort(newSort);
    setOrder(newOrder);
    startTransition(() => {
      const params = new URLSearchParams(searchParamsHook?.toString() || "");
      params.set("sort", newSort);
      params.set("order", newOrder);
      router.replace(`/offers?${params.toString()}`);
    });
  };

  // Handle minRating change (for both native select and custom dropdown)
  const handleMinRatingChange = (newMinRatingValue: string | number) => {
    const newMinRating = Number(newMinRatingValue);
    setMinRating(newMinRating);
    startTransition(() => {
      const params = new URLSearchParams(searchParamsHook?.toString() || "");
      params.set("minRating", String(newMinRating));
      router.replace(`/offers?${params.toString()}`);
    });
  };
  // Handle discountType change (for both native select and custom dropdown)
  const handlediscountTypeChange = (newDiscountType: string | number) => {
    const newType = String(newDiscountType);
    setDiscountType(newType);
    setMinDiscount(0);
    setMaxDiscount(100);
    startTransition(() => {
      const params = new URLSearchParams(searchParamsHook?.toString() || "");
      params.set("discountType", newType);
      router.replace(`/offers?${params.toString()}`);
    });
  };



  // Consolidated Clear Filters Handler
  const handleClearFilters = () => {
    // Category clear logic
    setSelectedCategories([]);

    // Store clear logic (single string)
    setSelectedStores("");

    setSearch("");
    setMinDiscount(0);
    setMaxDiscount(100);
    setMinRating(0);
    setDiscountType("");

    // Clear URL params
    startTransition(() => {
      const params = new URLSearchParams();
      // Keep only sort/order if not default
      if (sort !== "usedBy" || order !== "desc") {
        params.set("sort", sort);
        params.set("order", order);
      }
      router.replace(`/offers/?${params.toString()}`);
    });
  };

  return (
    <div className="max-w-7xl px-4 py-4 lg:p-8 mx-auto">
      <div className="inline-block mb-4">
        <h2 className="relative text-2xl text-gray-700 capitalize">
          All Offers
        </h2>
        <hr className="h-1 border-0 bg-primary-500 mt-2 w-[50%] rounded-lg" />
      </div>

      <div className="w-full">
        {/* Filter Container */}
        <div className="flex flex-col gap-4 mb-8 bg-white p-4 rounded-xl shadow-lg border border-gray-100">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search Bar */}

            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search offers or stores..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full px-4 py-2 pr-10 border-2 border-gray-200 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 focus:outline-none rounded-lg transition-all text-sm shadow-sm"
              />

              <button
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-primary-600 transition-colors"
                onClick={() => { /* empty */ }}
                aria-label="Search"
              >
                <SearchIcon className="h-4 w-4" />
              </button>
            </div>


            <div className="flex items-center gap-2 flex-1">
              <span className="font-semibold text-sm text-gray-700 whitespace-nowrap hidden md:inline">Category:</span>
              <select
                className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-primary-500 transition duration-200 shadow-sm text-sm"
                value={selectedCategories[0] || ""}
                onChange={(e) => {
                  setSelectedCategories(e.target.value ? [e.target.value] : []);
                }}
              >
                <option value="">Select a category</option>
                {allCategoryOptions.map(opt => (
                  <option
                    key={opt.value}
                    value={opt.value} 
                    className="py-1"
                  >
                    {opt.label} 
                  </option>
                ))}
              </select>
            </div>

            {/* 7. Store Dropdown*/}
            <div className="flex items-center gap-2 flex-1">
              <span className="font-semibold text-sm text-gray-700 whitespace-nowrap hidden md:inline">Store:</span>
              <select
                className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-primary-500 transition duration-200 shadow-sm text-sm"
                value={selectedStores}
                onChange={(e) => {
                  setSelectedStores(e.target.value);
                }}
              >
                <option value="">Select a store</option>

                {allStoreOptions.map(opt => (
                  <option
                    key={opt.value}
                    value={opt.value} 
                    className="py-1"
                  >
                    {opt.label} 
                  </option>
                ))}
              </select>
            </div>

          </div>

          {/* Original Row 2: Mobile/Desktop Sort/Type/Rating Filters */}
          <div className="flex flex-wrap gap-3 items-center w-full pb-3 border-b border-gray-100 md:border-b-0 md:pt-0">
            <div className="flex flex-wrap gap-3 w-full md:hidden">

              <MobileSelectDropdown
                label="Sort"
                options={SORT_OPTIONS}
                currentValue={currentSortValue}
                onChange={handleSortChange}
              />

              <MobileSelectDropdown
                label="Type"
                options={TYPE_OPTIONS}
                currentValue={discountType}
                onChange={handlediscountTypeChange}
              />

              <MobileSelectDropdown
                label="Rating"
                options={RATING_OPTIONS}
                currentValue={minRating}
                onChange={handleMinRatingChange}
              />

              {(selectedCategories.length > 0 || selectedStores || search || discountType || minRating > 0) && (
                <button
                  className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors shadow-sm whitespace-nowrap flex-1 min-w-[30%] mt-1"
                  onClick={handleClearFilters}
                >
                  Clear All Filters
                </button>
              )}
            </div>

            <div className="hidden md:flex flex-wrap gap-3 items-center w-full md:w-auto md:justify-end">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-gray-700 whitespace-nowrap">Sort by:</span>
                <select
                  value={currentSortValue}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 focus:outline-none rounded-lg text-sm bg-white text-gray-700 hover:border-primary-300 transition-colors"
                >
                  {SORT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Discount Type - Desktop */}
              <div className="flex gap-2 items-center">
                <span className="font-semibold text-sm text-gray-700 whitespace-nowrap">Type:</span>
                <select
                  value={discountType}
                  onChange={(e) => handlediscountTypeChange(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 focus:outline-none rounded-lg text-sm bg-white text-gray-700 hover:border-primary-300 transition-colors"
                >
                  {TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Min Rating - Desktop */}
              <div className="flex gap-2 items-center">
                <span className="font-semibold text-sm text-gray-700 whitespace-nowrap">Min Rating:</span>
                <select
                  value={minRating}
                  onChange={(e) => handleMinRatingChange(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 focus:outline-none rounded-lg text-sm bg-white text-gray-700 hover:border-primary-300 transition-colors"
                >
                  {RATING_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Clear Filters Button (Desktop) */}
              {(selectedCategories.length > 0 || selectedStores || search || discountType || minRating > 0) && (
                <button
                  className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors shadow-sm whitespace-nowrap"
                  onClick={handleClearFilters}
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </div>

          {/* Discount Ranges (Conditional) - Kept separate for better layout */}
          {discountType === "general" &&
            <div className="flex flex-wrap gap-4 items-center pt-2 border-t border-gray-100 md:border-t-0 md:pt-0">
              <div className="flex gap-2 items-center">
                <span className="font-semibold text-sm text-gray-700 whitespace-nowrap">Discount %:</span>
                <input type="number" min={0} max={maxDiscount} value={minDiscount} onChange={e => setMinDiscount(Number(e.target.value))}
                  className="w-16 px-2 py-1.5 border border-gray-300 focus:border-primary-400 focus:outline-none rounded-lg text-sm"
                />
                <span className="text-gray-500">-</span>
                <input type="number" min={minDiscount} max={100} value={maxDiscount} onChange={e => setMaxDiscount(Number(e.target.value))}
                  className="w-16 px-2 py-1.5 border border-gray-300 focus:border-primary-400 focus:outline-none rounded-lg text-sm"
                />
              </div>
            </div>
          }
        </div>
      </div>
      {/* End of Filters */}

      {/* Offers Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-200 rounded-xl h-48 w-full" aria-busy="true" />
          ))}
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={() => window.location.reload()} />
      ) : filteredOffers.length === 0 ? (
        <EmptyState message="No offers found for the selected filters." emoji="🎁" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredOffers.map((offer, i) => (
            <OfferCard
              key={offer._id || i}
              storeLogo={offer.storeIcon || "/assets/storeiconpng.png"}
              storeName={offer.store}
              title={offer.title}
              description={offer.description || ""}
              discountType={offer.discountType}
              discountTypeFlat={offer.discountTypeFlat || ""}
              discount={offer.discount || ""}
              discountTypeUnit={offer.discountTypeUnit || ""}
              oldValue={offer.oldValue || ""}
              oldValueUnit={offer.oldValueUnit || "price"}
              couponCode={offer.couponCode || ""}
              // offersCount={1}
              badge={offer.badge || ""}
              validTill={offer.validTill}
              slug={offer.slug || offer._id || ""}
              allowRedirectUrl={offer.allowRedirectUrl}
              redirectUrl={offer.redirectUrl}
              rating={offer.rating}
            />
          ))}
        </div>
      )}
      {hasMore && !loading && !isFetchingMore && (
        <div ref={loadMoreRef} className="text-center py-6">
          <span className="text-gray-400">Scroll to load more...</span>
        </div>
      )}
      {isFetchingMore && (
        <div className="text-center py-6">
          <span className="animate-pulse text-primary-500">Loading more offers...</span>
        </div>
      )}
    </div>
  );
}

export default function OffersPage() {
  return (
    <Suspense>
      <ClientOffersPage />
    </Suspense>
  );
}